import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    modalIsOpen: boolean;
    headlineText: React.ReactNode;
    messageText: React.ReactNode;
    cancelButtonText: string;
    submitButtonText: string;
    extendedStyles?: CENOrderApprovalRejectionModalStyles;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmit: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    submitTestSelector?: string;
    dataTestSelector?: string;
    setRemark: React.Dispatch<React.SetStateAction<string>>;
}

type Props = OwnProps;

export interface CENOrderApprovalRejectionModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    textGridItem?: GridItemProps;
    messageText?: TypographyProps;
    buttonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    addNotesRejectionGridItem?: GridItemProps;
    addNotesRejectionTextArea?: TextAreaProps;
}

export const cenOrderApprovalRejectionModalStyles: CENOrderApprovalRejectionModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    textGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            min-height: 32px !important;
            border-radius: 1.5em;
        `,
    },
    submitButton: {
        color: "secondary",
    },
    addNotesRejectionGridItem: {
        width: 12,
    },
    addNotesRejectionTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
            `,
        },
    },
};

class CENOrderApprovalRejectionModal extends React.Component<Props, { addNotesRejectionMessage: string }> {
    private readonly styles: CENOrderApprovalRejectionModalStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(cenOrderApprovalRejectionModalStyles, props.extendedStyles);
        this.state = {
            addNotesRejectionMessage: "",
        };
    }

    formCancelHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        this.props.onCancel(e);
    };

    formSubmitHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        this.props.onSubmit(e);
    };

    addNotesRejectionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ addNotesRejectionMessage: event.target.value });
        this.props.setRemark(event.target.value);
    };
    render() {
        const styles = this.styles;

        return (
            <Modal
                headline={this.props.headlineText}
                {...styles.modal}
                isOpen={this.props.modalIsOpen}
                data-test-selector={this.props.dataTestSelector}
                handleClose={this.formCancelHandler}
            >
                <GridContainer {...styles.container}>
                    <GridItem {...styles.textGridItem}>
                        <Typography {...styles.messageText} data-test-selector="twoButtonModal_MessageContent">
                            {this.props.messageText}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.addNotesRejectionGridItem}>
                        <TextArea
                            label={translate("Please provide a reason for rejecting order")}
                            value={this.state.addNotesRejectionMessage}
                            onChange={this.addNotesRejectionChangeHandler}
                            data-test-selector="addNotesRejectionMessage"
                            {...styles.addNotesRejectionTextArea}
                        />
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem}>
                        <Button {...styles.cancelButton} onClick={this.formCancelHandler}>
                            {this.props.cancelButtonText}
                        </Button>
                        <Button
                            {...styles.submitButton}
                            onClick={this.formSubmitHandler}
                            data-test-selector={this.props.submitTestSelector}
                        >
                            {this.props.submitButtonText}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        );
    }
}

export default CENOrderApprovalRejectionModal;
